import type {
  CompProps,
  CustomCompType,
  GenericFieldConfig,
  GenericFormType,
} from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { OptionalLabel } from '@neo/primitives/label'
import { PhoneNumberInput } from '@neo/primitives/phone-number-input'
import { Tooltip } from '@neo/primitives/tooltip'
import icContact from '@shared/assets/icons/ic-contact.svg'
import icLink from '@shared/assets/icons/ic-link.svg'
import headerDocs from '@shared/assets/illustrations/header-docs.svg'
import headerImage from '@shared/assets/illustrations/header-image.svg'
import headerVideo from '@shared/assets/illustrations/ic-play-icon.svg'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { ExternalLink, Info, InfoIcon, PlusIcon, X } from 'lucide-react'
import type { ElementType } from 'react'
import { v4 } from 'uuid'
import { ChatInput } from 'v2/shared/components/chat-input'
import type { WhatsAppAccount } from 'v2/types/api/settings'
import z from 'zod'
import type { WATemplateFormValues } from './types'

export const getImageName = {
  VIDEO: headerVideo,
  IMAGE: headerImage,
  DOCUMENT: headerDocs,
  URL: icLink,
  PHONE_NUMBER: icContact,
}
export const AUTH_TEMPLATE_BODY_TEXT = '{{1}} is your verification code.'
export const AUTH_TEMPLATE_SECURITY_SUFFIX = ' For your security, do not share this code.'

const whenNonAuth = (schema: z.ZodTypeAny) => (values: WATemplateFormValues) =>
  values.category === 'AUTHENTICATION' ? z.any() : schema

const whenAuth = (schema: z.ZodTypeAny) => (values: WATemplateFormValues) =>
  values.category === 'AUTHENTICATION' ? schema : z.any()

// Noun used in the media-header hint copy, keyed by the selected header type. Only media header
// types have an entry; TEXT/NONE map to undefined, which is how the hint stays hidden for them.
export const mediaHeaderNoun: Partial<Record<WATemplateFormValues['header']['type'], string>> = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
}

// When the header type is a media type (Image/Video/Document) there is intentionally no upload
// field here — we handle template setup, and the customer adds their own media when they send the
// message. Without this note customers who picked "Image" looked for a missing upload control and
// assumed the form was broken. Copy stays product-facing: no mention of samples or Meta review.
const MediaHeaderHint = ({ form }: { form: GenericFormType<WATemplateFormValues> }) => {
  const headerType = form.useStore((state) => state.values.header.type)
  const noun = mediaHeaderNoun[headerType]
  if (!noun) return null
  return (
    <div className="col-span-full flex items-start gap-2 rounded-md border border-st2 bg-bg2 px-3 py-2.5">
      <Info className="w-4 h-4 mt-0.5 shrink-0 text-secondary" />
      <span className="text-s-400 text-secondary">
        No upload needed here — we handle it. Just add your {noun} when you send the message.
      </span>
    </div>
  )
}

export const validationSchema = z.object({
  templateName: z
    .string()
    .min(1, 'Name is required')
    .refine(
      (val) => /^[a-z0-9_]*$/.test(val),
      'Only letters, numbers, and underscores are allowed',
    ),
  waBusinessAccount: z.string().min(1, 'WhatsApp business account is required'),
  category: z.enum(['MARKETING', 'UTILITY', 'AUTHENTICATION']),
  language: z.string().min(1, 'Language is required'),
  header: z.object({
    type: z.enum(['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'NONE']),
    content: z
      .string({ message: 'Header text is required' })
      .min(1, 'Header text is required')
      .max(60, 'Header text cannot exceed 60 characters'),
    mediaUrl: z.string().optional(),
  }),
  body: z.string().min(1, 'Body is required').max(1024, 'Body cannot exceed 1024 characters'),
  footer: z.string().max(60, 'Footer cannot exceed 60 characters').optional(),
  buttonType: z.enum(['QUICK_REPLY', 'CALL_TO_ACTION', 'NONE']),
  buttons: z.array(z.any()).refine(
    (data) => {
      const buttonType = data[0]?.type
      const maxButtons = buttonType === 'QUICK_REPLY' ? 3 : 2
      return data.length >= 1 && data.length <= maxButtons
    },
    (data) => {
      const buttonType = data[0]?.type
      const maxButtons = buttonType === 'QUICK_REPLY' ? 3 : 2
      if (data.length < 1 && buttonType !== 'NONE') {
        return {
          message: 'Minimum 1 button required',
          path: ['buttons'],
        }
      }
      return {
        message: `Maximum ${maxButtons} buttons allowed`,
        path: ['buttons'],
      }
    },
  ),
  addSecurityRecommendation: z.boolean(),
  codeExpirationMinutes: z
    .number({ message: 'Code expiration must be a number' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1 minute')
    .max(90, 'Must be at most 90 minutes')
    .optional(),
})
export const languageList = [
  {
    code: 'af',
    name: 'Afrikaans',
  },
  {
    code: 'sq',
    name: 'Albanian',
  },
  {
    code: 'ar',
    name: 'Arabic',
  },
  {
    code: 'az',
    name: 'Azerbaijani',
  },
  {
    code: 'bn',
    name: 'Bengali',
  },
  {
    code: 'bg',
    name: 'Bulgarian',
  },
  {
    code: 'ca',
    name: 'Catalan',
  },
  {
    code: 'zh_CN',
    name: 'Chinese (CHN)',
  },
  {
    code: 'zh_HK',
    name: 'Chinese (HKG)',
  },
  {
    code: 'zh_TW',
    name: 'Chinese (TAI)',
  },
  {
    code: 'hr',
    name: 'Croatian',
  },
  {
    code: 'cs',
    name: 'Czech',
  },
  {
    code: 'da',
    name: 'Danish',
  },
  {
    code: 'nl',
    name: 'Dutch',
  },
  {
    code: 'en',
    name: 'English',
  },
  {
    code: 'en_GB',
    name: 'English (UK)',
  },
  {
    code: 'en_US',
    name: 'English (US)',
  },
  {
    code: 'et',
    name: 'Estonian',
  },
  {
    code: 'fil',
    name: 'Filipino',
  },
  {
    code: 'fi',
    name: 'Finnish',
  },
  {
    code: 'fr',
    name: 'French',
  },
  {
    code: 'de',
    name: 'German',
  },
  {
    code: 'el',
    name: 'Greek',
  },
  {
    code: 'gu',
    name: 'Gujarati',
  },
  {
    code: 'ha',
    name: 'Hausa',
  },
  {
    code: 'he',
    name: 'Hebrew',
  },
  {
    code: 'hi',
    name: 'Hindi',
  },
  {
    code: 'hu',
    name: 'Hungarian',
  },
  {
    code: 'id',
    name: 'Indonesian',
  },
  {
    code: 'ga',
    name: 'Irish',
  },
  {
    code: 'it',
    name: 'Italian',
  },
  {
    code: 'ja',
    name: 'Japanese',
  },
  {
    code: 'kn',
    name: 'Kannada',
  },
  {
    code: 'kk',
    name: 'Kazakh',
  },
  {
    code: 'ko',
    name: 'Korean',
  },
  {
    code: 'lo',
    name: 'Lao',
  },
  {
    code: 'lv',
    name: 'Latvian',
  },
  {
    code: 'lt',
    name: 'Lithuanian',
  },
  {
    code: 'mk',
    name: 'Macedonian',
  },
  {
    code: 'ms',
    name: 'Malay',
  },
  {
    code: 'ml',
    name: 'Malayalam',
  },
  {
    code: 'mr',
    name: 'Marathi',
  },
  {
    code: 'nb',
    name: 'Norwegian',
  },
  {
    code: 'fa',
    name: 'Persian',
  },
  {
    code: 'pl',
    name: 'Polish',
  },
  {
    code: 'pt_BR',
    name: 'Portuguese (BR)',
  },
  {
    code: 'pt_PT',
    name: 'Portuguese (POR)',
  },
  {
    code: 'pa',
    name: 'Punjabi',
  },
  {
    code: 'ro',
    name: 'Romanian',
  },
  {
    code: 'ru',
    name: 'Russian',
  },
  {
    code: 'sr',
    name: 'Serbian',
  },
  {
    code: 'sk',
    name: 'Slovak',
  },
  {
    code: 'sl',
    name: 'Slovenian',
  },
  {
    code: 'es',
    name: 'Spanish',
  },
  {
    code: 'es_AR',
    name: 'Spanish (ARG)',
  },
  {
    code: 'es_ES',
    name: 'Spanish (SPA)',
  },
  {
    code: 'es_MX',
    name: 'Spanish (MEX)',
  },
  {
    code: 'sw',
    name: 'Swahili',
  },
  {
    code: 'sv',
    name: 'Swedish',
  },
  {
    code: 'ta',
    name: 'Tamil',
  },
  {
    code: 'te',
    name: 'Telugu',
  },
  {
    code: 'th',
    name: 'Thai',
  },
  {
    code: 'tr',
    name: 'Turkish',
  },
  {
    code: 'uk',
    name: 'Ukrainian',
  },
  {
    code: 'ur',
    name: 'Urdu',
  },
  {
    code: 'uz',
    name: 'Uzbek',
  },
  {
    code: 'vi',
    name: 'Vietnamese',
  },
  {
    code: 'zu',
    name: 'Zulu',
  },
]

export const getFields = (
  waTemplateConfig: WhatsAppAccount[],
  opts?: { lockedCategory?: WATemplateFormValues['category'] },
) => {
  const fields: GenericFieldConfig<WATemplateFormValues> = [
    {
      name: 'header1',
      type: 'slot',
      component: <div className="text-p-600">Template configuration</div>,
    },
    {
      name: 'templateName',
      label: 'Name',
      componentType: 'input',
      zodValidation: validationSchema.shape.templateName,
      eventDataExtractor: (e) =>
        z
          .string({ message: 'Template name is required' })
          .transform((val) => val.replace(/\s+/g, '_').toLowerCase())
          .parse(e.target?.value),
    },
    {
      name: 'waBusinessAccount',
      label: 'WhatsApp Business Account',
      componentType: 'select',
      zodValidation: validationSchema.shape.waBusinessAccount,
      inputProps: {
        placeholder: 'Select WhatsApp Business Account',
        options:
          waTemplateConfig?.map((config) => ({
            label: config.whatsapp_account_name,
            value: config.whatsapp_account_id,
          })) || [],
      },
    },
    {
      name: 'category',
      label: 'Category',
      componentType: 'select',
      // Hidden when the caller locks the category (e.g. Verify locks AUTHENTICATION).
      visible: () => !opts?.lockedCategory,
      zodValidation: validationSchema.shape.category,
      afterOnChange: (values, _i, form) => {
        if (values.category === 'AUTHENTICATION') {
          form?.setFieldValue('header', { type: 'NONE' })
          form?.setFieldValue('body', '')
          form?.setFieldValue('footer', '')
          form?.setFieldValue('buttonType', 'NONE')
          form?.setFieldValue('buttons', [])
          if (!form?.state.values.otpButton?.otp_type) {
            form?.setFieldValue('otpButton.otp_type', 'ZERO_TAP')
            form?.setFieldValue('otpButton.text', 'Zero-tap autofill')
          }
          const otpType = form?.state.values.otpButton?.otp_type
          const needsApp = otpType === 'ONE_TAP' || otpType === 'ZERO_TAP'
          if (needsApp && !form?.state.values.otpButton?.autofill_text) {
            form?.setFieldValue('otpButton.autofill_text', 'Autofill')
          }
          if (needsApp && !form?.state.values.otpSupportedApps?.length) {
            form?.setFieldValue('otpSupportedApps', [{ package_name: '', signature_hash: '' }])
          }
          form?.validateField('otpButton.otp_type', 'change')
          form?.validateField('otpButton.text', 'change')
          form?.validateField('otpButton.autofill_text', 'change')
          form?.validateField('otpSupportedApps', 'change')
        }
      },
      inputProps: {
        options: [
          { label: 'Marketing', value: 'MARKETING' },
          { label: 'Utility', value: 'UTILITY' },
          { label: 'Authentication', value: 'AUTHENTICATION' },
        ],
      },
    },
    {
      name: 'language',
      label: 'Language',
      componentType: 'select',
      zodValidation: validationSchema.shape.language,
      inputProps: {
        options: languageList.map((lang) => ({ label: lang.name, value: lang.code })),
      },
    },
    {
      name: 'hr',
      type: 'slot',
      component: <hr className="border-t border-border col-span-full" />,
    },
    {
      name: 'header2',
      type: 'slot',
      component: <div className="text-p-600">Template content</div>,
    },
    {
      name: 'header.type',
      label: 'Header Type',
      componentType: 'select',
      onChangeListenTo: ['category'],
      visible: (values) => values.category !== 'AUTHENTICATION',
      zodValidation: validationSchema.shape.header.shape.type,
      afterOnChange: (_values, _i, form) => {
        form?.setFieldValue('header.content', '')
      },
      inputProps: {
        options: [
          { label: 'Text', value: 'TEXT' },
          { label: 'Image', value: 'IMAGE' },
          { label: 'Video', value: 'VIDEO' },
          { label: 'Document', value: 'DOCUMENT' },
          { label: 'None', value: 'NONE' },
        ],
      },
    },
    {
      name: 'mediaHeaderHint',
      type: 'basic',
      component: ({ form }) => <MediaHeaderHint form={form} />,
    },
    {
      name: 'header.content',
      componentType: 'input',
      onChangeListenTo: ['header.type', 'language', 'category'],
      visible: (values) => values.category !== 'AUTHENTICATION' && values.header.type === 'TEXT',
      zodValidation: (values) =>
        values.category !== 'AUTHENTICATION' && values.header.type === 'TEXT'
          ? validationSchema.shape.header.shape.content
          : z.any(),
      getDynamicProps: (values, _i, form) => {
        const hasVariable = /\{\{\d+\}\}/.test(values.header.content || '')
        return {
          inputProps: {
            maxLength: 60,
            label: (
              <div className="flex justify-between">
                <span>Header text</span>
                <Button
                  disabled={hasVariable}
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  data-testid="button-add-header-variable"
                  type="button"
                  onClick={() => {
                    form?.setFieldValue('header.content', `${values.header.content} {{1}}`)
                    form?.validateField('header.content', 'change')
                  }}
                >
                  Add variable
                </Button>
              </div>
            ),
            className: '[&_input]:pr-16',
            placeholder: `Enter header text in ${languageList.find((lang) => lang.code === values.language)?.name}`,
            endIcon: (
              <span className="text-xs-400 font-jetbrains tabular-nums text-muted-foreground mr-4">
                {values.header.content?.length || 0} / 60
              </span>
            ),
          },
        }
      },
    },
    {
      name: 'body',
      label: 'Body',
      type: 'custom',
      eventDataExtractor: (e) => e,
      component: ChatInput as CustomCompType<WATemplateFormValues>,
      onChangeListenTo: ['category'],
      visible: (values) => values.category !== 'AUTHENTICATION',
      zodValidation: whenNonAuth(validationSchema.shape.body),
    },
    {
      name: 'addSecurityRecommendation',
      label: 'Add security recommendation',
      componentType: 'checkbox',
      onChangeListenTo: ['category'],
      visible: (values) => values.category === 'AUTHENTICATION',
      zodValidation: validationSchema.shape.addSecurityRecommendation,
    },
    {
      name: 'footer',
      label: 'Footer',
      componentType: 'input',
      onChangeListenTo: ['category'],
      visible: (values) => values.category !== 'AUTHENTICATION',
      zodValidation: whenNonAuth(validationSchema.shape.footer),
      inputProps: {
        maxLength: 60,
      },
      getDynamicProps: (values) => ({
        inputProps: {
          placeholder: 'Footer Text (Optional)',
          className: '[&_input]:pr-16',
          endIcon: (
            <span className="text-xs-400 font-jetbrains tabular-nums text-muted-foreground mr-4">
              {values.footer?.length || 0} / 60
            </span>
          ),
        },
      }),
    },
    {
      name: 'codeExpirationMinutes',
      componentType: 'inputNumber',
      onChangeListenTo: ['category'],
      visible: (values) => values.category === 'AUTHENTICATION',
      zodValidation: whenAuth(validationSchema.shape.codeExpirationMinutes.nullable()),
      inputProps: {
        type: 'number',
        label: <OptionalLabel>Code expiration (minutes)</OptionalLabel>,
        placeholder: '1 - 90 ',
        endIcon: <span className="text-s mr-5">Minutes</span>,
        min: 1,
        max: 90,
      },
    },
    {
      name: 'buttonType',
      label: 'Button Type',
      componentType: 'select',
      onChangeListenTo: ['category'],
      visible: (values) => values.category !== 'AUTHENTICATION',
      zodValidation: whenNonAuth(validationSchema.shape.buttonType),
      afterOnChange: (values, _i, form) => {
        form?.setFieldValue('buttons', [
          values.buttonType === 'QUICK_REPLY'
            ? { text: 'Button 1', type: 'QUICK_REPLY', uuid: v4() }
            : { text: 'Click here', type: 'URL', url: '', url_type: 'STATIC', uuid: v4() },
        ])
        form?.validateField('buttons', 'change')
      },
      inputProps: {
        options: [
          { label: 'Quick Reply', value: 'QUICK_REPLY' },
          { label: 'Call to Action', value: 'CALL_TO_ACTION' },
          { label: 'None', value: 'NONE' },
        ],
      },
    },
    {
      name: 'otpButton.otp_type',
      label: 'OTP Type',
      componentType: 'radioGroup',
      onChangeListenTo: ['category'],
      visible: (values) => values.category === 'AUTHENTICATION',
      zodValidation: whenAuth(
        z.enum(['COPY_CODE', 'ONE_TAP', 'ZERO_TAP'], { message: 'OTP type is required' }),
      ),
      afterOnChange: (values, _i, form) => {
        const otpType = values.otpButton?.otp_type
        const needsApp = otpType === 'ONE_TAP' || otpType === 'ZERO_TAP'
        // text is always "Copy code" — Meta uses this as the fallback button label
        form?.setFieldValue('otpButton.text', 'Copy code')
        if (needsApp) {
          if (!values.otpButton?.autofill_text) {
            form?.setFieldValue('otpButton.autofill_text', 'Autofill')
          }
          if (!values.otpSupportedApps?.length) {
            form?.setFieldValue('otpSupportedApps', [{ package_name: '', signature_hash: '' }])
          }
        } else {
          // COPY_CODE: clear ONE_TAP/ZERO_TAP-only fields so dependent UI hides
          form?.setFieldValue('otpButton.autofill_text', '')
          form?.setFieldValue('otpSupportedApps', [])
          form?.setFieldValue('otpButton.zero_tap_terms_accepted', false)
        }
        form?.validateField('otpButton.autofill_text', 'change')
        form?.validateField('otpSupportedApps', 'change')
      },
      inputProps: {
        optionsClassName: 'flex flex-col gap-3',
        options: [
          {
            value: 'ZERO_TAP',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Zero-tap autofill
                <Tooltip content="Auto-fill only works when your customer requests a code from the same device that has their WhatsApp account. If autofill isn't possible, a copy code message will be sent.">
                  <Info className="w-3.5 h-3.5 text-secondary" />
                </Tooltip>
                <span></span>
              </span>
            ),
          },
          {
            value: 'ONE_TAP',
            label: (
              <span className="inline-flex items-center gap-1.5">
                One-tap autofill
                <Tooltip content="Auto-fill only works when your customer requests a code from the same device that has their WhatsApp account. If autofill isn't possible, a copy code message will be sent.">
                  <Info className="w-3.5 h-3.5 text-secondary" />
                </Tooltip>
              </span>
            ),
          },
          {
            value: 'COPY_CODE',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Copy code
                <Tooltip content="Customers tap the button to copy the code and then paste it in your app themselves. Copy code works best for customers who request the code on a device that does not have their WhatsApp account.">
                  <Info className="w-3.5 h-3.5 text-secondary" />
                </Tooltip>
              </span>
            ),
          },
        ],
      },
    },
    {
      name: 'otpButton.autofill_text',
      label: 'Autofill Text',
      componentType: 'input',
      onChangeListenTo: ['category', 'otpButton.otp_type'],
      visible: (values) =>
        values.category === 'AUTHENTICATION' && values.otpButton?.otp_type === 'ONE_TAP',
      zodValidation: (values) =>
        values.category === 'AUTHENTICATION' && values.otpButton?.otp_type === 'ONE_TAP'
          ? z.string().min(1, 'Autofill text is required').max(25, 'Max 25 characters')
          : z.any(),
    },
    {
      name: 'otpSupportedApps',
      type: 'array',
      className: 'col-span-full flex gap-2 items-start',
      removeItemButton: <X className="w-4 h-4 translate-y-7" />,
      onChangeListenTo: ['category', 'otpButton.otp_type'],
      visible: (values) =>
        values.category === 'AUTHENTICATION' &&
        (values.otpButton?.otp_type === 'ONE_TAP' || values.otpButton?.otp_type === 'ZERO_TAP'),
      defaultNewValue: () => ({ package_name: '', signature_hash: '' }),
      addItemButton: ({ state, ...rest }) => {
        const count = state.otpSupportedApps?.length || 0
        if (count >= 5) return null
        return (
          <div className="col-span-full">
            <Button variant="outline" type="button" {...rest}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add app
            </Button>
          </div>
        )
      },
      zodValidation: (values) => {
        if (
          values.category !== 'AUTHENTICATION' ||
          (values.otpButton?.otp_type !== 'ONE_TAP' && values.otpButton?.otp_type !== 'ZERO_TAP')
        ) {
          return z.any()
        }
        return z
          .array(z.any())
          .min(1, 'Add at least one app')
          .max(5, 'You can add up to 5 apps')
          .refine(
            (apps) => {
              const seen = new Set<string>()
              for (const a of apps as Array<{ package_name: string; signature_hash: string }>) {
                if (!a.package_name || !a.signature_hash) continue
                const key = `${a.package_name}|${a.signature_hash}`
                if (seen.has(key)) return false
                seen.add(key)
              }
              return true
            },
            { message: 'Each app must have a unique package name and signature hash' },
          )
      },
      subFields: [
        {
          name: 'otpSupportedApps[0].package_name',
          label: 'Package name',
          componentType: 'input',
          className: 'flex-1',
          zodValidation: z
            .string()
            .min(1, 'Package name is required')
            .max(224, 'Max 224 characters'),
          inputProps: {
            placeholder: 'com.example.myapplication',
            maxLength: 224,
          },
        },
        {
          name: 'otpSupportedApps[0].signature_hash',
          label: 'App signature hash',
          componentType: 'input',
          className: 'flex-1',
          zodValidation: z.string().length(11, 'Your signature hash must be 11 characters long.'),
          inputProps: {
            placeholder: 'Enter text',
            maxLength: 11,
          },
        },
      ],
    },
    {
      name: 'otpButton.zero_tap_terms_accepted',
      componentType: 'checkbox',
      onChangeListenTo: ['category', 'otpButton.otp_type', 'waBusinessAccount'],
      visible: (values) =>
        values.category === 'AUTHENTICATION' && values.otpButton?.otp_type === 'ZERO_TAP',
      zodValidation: (values) =>
        values.category === 'AUTHENTICATION' && values.otpButton?.otp_type === 'ZERO_TAP'
          ? z.boolean().refine((v) => v === true, {
              message: 'You must accept the WhatsApp Business Terms for Zero-tap',
            })
          : z.any(),
      getDynamicProps: (values) => {
        const wabaName =
          waTemplateConfig.find((c) => c.whatsapp_account_id === values.waBusinessAccount)
            ?.whatsapp_account_name || 'My WhatsApp Business Account'
        return {
          inputProps: {
            label: (
              <span className="text-s-400">
                <Tooltip
                  // @ts-ignore tooltip content accepts ReactNode
                  content={
                    <span>
                      By selecting zero-tap, you confirm that {wabaName}'s use of zero-tap
                      authentication is subject to these terms. It's {wabaName}'s responsibility to
                      ensure customers expect the code to be auto-filled on their behalf when they
                      choose Zero-tap on WhatsApp.
                    </span>
                  }
                >
                  <span className="inline-flex items-center gap-1 flex-wrap">
                    I accept the{' '}
                    <a
                      href="https://www.whatsapp.com/legal/business-terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      WhatsApp Business Terms of Service
                    </a>
                    for Zero-tap.
                    <InfoIcon size={16} />
                  </span>
                </Tooltip>{' '}
                <a
                  href="https://business.facebook.com/business/help/285737223876109"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs-400 inline-flex items-center gap-1 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn more and review best practices
                  <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            ),
          },
        }
      },
    },
    {
      name: 'buttons',
      type: 'array',
      className: 'relative flex flex-wrap gap-2 items-start pr-7',
      removeItemButton: <X className="w-4 h-4 absolute right-0 top-8" />,
      onChangeListenTo: ['buttonType', 'category'],
      visible: (values) => values.category !== 'AUTHENTICATION' && values.buttonType !== 'NONE',
      defaultNewValue: (form) => {
        if (form.state.values.buttonType === 'CALL_TO_ACTION') {
          const existingButtons = form.state.values.buttons || []
          const hasUrlButton = existingButtons.some((button) => button.type === 'URL')

          if (hasUrlButton) {
            return {
              text: 'Call Now',
              type: 'PHONE_NUMBER',
              phone_number: '',
              uuid: v4(),
            }
          }
          return {
            text: 'Click here',
            type: 'URL',
            url: '',
            url_type: 'STATIC',
            uuid: v4(),
          }
        }
        return {
          text: `Button ${form.state.values.buttons.length + 1}`,
          type: 'QUICK_REPLY',
          uuid: v4(),
        }
      },
      addItemButton: ({ state, ...rest }) => {
        if (state.buttonType === 'NONE') return null
        return (
          <div className="col-span-full">
            <Button variant="outline" type="button" {...rest}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Button
            </Button>
          </div>
        )
      },
      zodValidation: (values) => {
        if (values.category === 'AUTHENTICATION') return z.any()
        if (values.buttonType === 'NONE') return z.any()
        return validationSchema.shape.buttons
      },
      subFields: [
        {
          name: 'buttons[0].type',
          label: 'Button Type',
          onChangeListenTo: ['buttonType'],
          componentType: 'select',
          className: 'w-[95px]',
          inputProps: {
            options: [
              { label: 'URL', value: 'URL' },
              { label: 'Phone', value: 'PHONE_NUMBER' },
            ],
          },
          zodValidation: z.enum(['QUICK_REPLY', 'URL', 'PHONE_NUMBER'], {
            message: 'Button type is required',
          }),
          visible: (values) => values.buttonType !== 'QUICK_REPLY',
        },
        {
          name: 'buttons[0].text',
          label: 'Button Text',
          componentType: 'input',
          zodValidation: z.string().min(1, 'Button text is required').max(25, 'Max 25 characters'),
        },
        {
          name: 'buttons[0].url_type',
          label: 'URL Type',
          componentType: 'select',
          type: 'sub-item',
          className: 'w-[110px]',
          onChangeListenTo: (_state, i) => [`buttons[${i as number}].type`],
          inputProps: {
            options: [
              { label: 'Static', value: 'STATIC' },
              { label: 'Dynamic', value: 'DYNAMIC' },
            ],
          },
          zodValidation: z.enum(['STATIC', 'DYNAMIC']).optional(),
          visible: (values, i) => values.buttons[i as number].type === 'URL',
        },
        {
          name: 'buttons[0].url',
          componentType: 'input',
          label: 'URL',
          type: 'sub-item',
          className: 'flex-1 min-w-[220px]',
          inputProps: {
            inputMode: 'url',
          },
          onChangeListenTo: (_state, i) => [
            `buttons[${i as number}].type`,
            `buttons[${i as number}].url_type`,
          ],
          // For dynamic URLs the {{1}} variable is shown as a fixed suffix (like Meta) and
          // appended automatically on submit — the user types only the base URL. The label
          // tooltip and suffix are only relevant in that mode, so they live in dynamic props.
          getDynamicProps: (values, i) =>
            values.buttons[i as number]?.url_type === 'DYNAMIC'
              ? {
                  inputProps: {
                    inputMode: 'url',
                    label: (
                      <span className="inline-flex items-center gap-1">
                        URL
                        <Tooltip content="Adding a variable creates a personalised link for each customer. Only one variable is allowed and it is added automatically to the end of the URL.">
                          <Info className="w-3.5 h-3.5 text-secondary" />
                        </Tooltip>
                      </span>
                    ),
                    endIcon: (
                      <span className="text-s-500 text-primary mr-3 whitespace-nowrap">
                        {'{{1}}'}
                      </span>
                    ),
                  },
                }
              : {},
          zodValidation: (values, i) => {
            const button = values.buttons[i as number]
            if (button.type !== 'URL') return z.any()
            const urlSchema = z.string().url('Invalid URL')
            return button.url_type === 'DYNAMIC'
              ? urlSchema.refine(
                  (val) => !/\{\{/.test(val),
                  'Do not add the variable — {{1}} is added automatically at the end',
                )
              : urlSchema
          },
          visible: (values, i) => values.buttons[i as number].type === 'URL',
        },
        {
          name: 'buttons[0].example',
          componentType: 'input',
          label: (
            <div className="flex flex-col gap-0.5">
              <span className="inline-flex items-center gap-1">
                Sample URL
                <Tooltip content="Enter the full URL with the variable replaced by a real value, e.g. https://www.example.com/cart/abc123. Don't use real customer information.">
                  <Info className="w-3.5 h-3.5 text-secondary" />
                </Tooltip>
              </span>
              <span className="text-xs-400 text-secondary">
                Add an example of the website URL so Meta can review your template.
              </span>
            </div>
          ),
          type: 'sub-item',
          className: 'basis-full',
          onChangeListenTo: (_state, i) => [
            `buttons[${i as number}].type`,
            `buttons[${i as number}].url_type`,
            `buttons[${i as number}].url`,
          ],
          getDynamicProps: (values, i) => ({
            inputProps: {
              placeholder: `Enter full URL for ${
                values.buttons[i as number]?.url || 'https://www.example.com/'
              }{{1}}`,
            },
          }),
          // Meta normalises and is lenient about the sample (it just needs to be a valid URL),
          // so we only require a valid URL here and let Meta validate the finer details — this
          // avoids rejecting samples Meta would accept (e.g. the base domain itself).
          zodValidation: (values, i) => {
            const button = values.buttons[i as number]
            if (button.type !== 'URL' || button.url_type !== 'DYNAMIC') return z.any()
            return z.string({ message: 'Sample URL is required' }).url('Enter a valid URL')
          },
          visible: (values, i) =>
            values.buttons[i as number].type === 'URL' &&
            values.buttons[i as number].url_type === 'DYNAMIC',
        },
        {
          name: 'buttons[0].phone_number',
          label: 'Phone Number',
          className: 'flex-1',
          type: 'sub-item',
          onChangeListenTo: (_state, i) => [`buttons[${i as number}].type`],
          zodValidation: z
            .string()
            .refine((value) => {
              const phoneNumber = parsePhoneNumberFromString(value)
              return phoneNumber?.isValid() ?? false
            }, 'Invalid phone number')
            .optional(),
          visible: (values, i) => values.buttons[i as number].type === 'PHONE_NUMBER',
          component: (({ value, onChange, disabled }) => (
            <PhoneNumberInput
              autoDetectCountry={false}
              value={value as string}
              onChange={onChange as (e: React.ChangeEvent<HTMLInputElement>) => void}
              disabled={disabled}
              label="Phone Number"
            />
          )) as ElementType<CompProps<WATemplateFormValues>>,
          eventDataExtractor: (e) => e.target?.value,
        },
      ],
    },
  ]
  return fields
}
export const getPreviewMode = {
  light_mode: '-light',
  dark_mode: '-dark',
}

export const statusVariants = {
  approved: 'success',
  pending: 'warning',
  rejected: 'destructive',
  draft: 'default',
  created: 'default',
} as const

export const numberConstant = {
  headerTextLength: 53,
  bodyMaxLength: 1024,
  bodyDisableLength: 1023,
  bodyAddVariable: 1017,
  enableBodyButtonConst: 1022,
}
